import React, { createContext, useCallback, useContext, useLayoutEffect, useState, FC } from 'react'

export type ThemeMode = 'light' | 'dark'
export type ColorTheme =
  | 'violet'
  | 'ocean'
  | 'emerald'
  | 'sunset'
  | 'rose'
  | 'pastel-lavender'
  | 'pastel-sky'
  | 'pastel-mint'
  | 'pastel-peach'
  | 'pastel-blush'

export type ColorThemeGroup = 'vivid' | 'pastel'

export const COLOR_THEME_VIVID: { id: ColorTheme; label: string; swatch: string; group: ColorThemeGroup }[] = [
  { id: 'violet', label: 'Violet', swatch: '#6659ff', group: 'vivid' },
  { id: 'ocean', label: 'Ocean', swatch: '#0ea5e9', group: 'vivid' },
  { id: 'emerald', label: 'Emerald', swatch: '#10b981', group: 'vivid' },
  { id: 'sunset', label: 'Sunset', swatch: '#f97316', group: 'vivid' },
  { id: 'rose', label: 'Rose', swatch: '#f43f5e', group: 'vivid' },
]

export const COLOR_THEME_PASTEL: { id: ColorTheme; label: string; swatch: string; group: ColorThemeGroup }[] = [
  { id: 'pastel-lavender', label: 'Lavender', swatch: '#c4b5fd', group: 'pastel' },
  { id: 'pastel-sky', label: 'Sky', swatch: '#7dd3fc', group: 'pastel' },
  { id: 'pastel-mint', label: 'Mint', swatch: '#6ee7b7', group: 'pastel' },
  { id: 'pastel-peach', label: 'Peach', swatch: '#fdba74', group: 'pastel' },
  { id: 'pastel-blush', label: 'Blush', swatch: '#f9a8d4', group: 'pastel' },
]

export const COLOR_THEME_OPTIONS = [...COLOR_THEME_VIVID, ...COLOR_THEME_PASTEL]

type LayoutContextState = {
  sidebarCollapsed: boolean
  toggleSidebar: () => void
  setSidebarCollapsed: (collapsed: boolean) => void
  themeMode: ThemeMode
  colorTheme: ColorTheme
  toggleThemeMode: () => void
  setColorTheme: (theme: ColorTheme) => void
}

const contextDefaultValues: LayoutContextState = {
  sidebarCollapsed: false,
  toggleSidebar: () => {},
  setSidebarCollapsed: () => {},
  themeMode: 'light',
  colorTheme: 'violet',
  toggleThemeMode: () => {},
  setColorTheme: () => {},
}

const THEME_MODE_KEY = 'ams-theme-mode'
const COLOR_THEME_KEY = 'ams-color-theme'

const readStoredThemeMode = (): ThemeMode => {
  const stored = localStorage.getItem(THEME_MODE_KEY)
  return stored === 'dark' ? 'dark' : 'light'
}

const readStoredColorTheme = (): ColorTheme => {
  const stored = localStorage.getItem(COLOR_THEME_KEY) as ColorTheme | null
  return COLOR_THEME_OPTIONS.some(option => option.id === stored) ? stored! : 'violet'
}

const applyThemeToDocument = (themeMode: ThemeMode, colorTheme: ColorTheme) => {
  document.documentElement.setAttribute('data-theme', themeMode)
  document.documentElement.setAttribute('data-color-theme', colorTheme)
}

if (typeof document !== 'undefined') {
  applyThemeToDocument(readStoredThemeMode(), readStoredColorTheme())
}

export const LayoutContext = createContext<LayoutContextState>(contextDefaultValues)

export const LayoutProvider: FC = ({ children }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [themeMode, setThemeMode] = useState<ThemeMode>(() => readStoredThemeMode())
  const [colorTheme, setColorThemeState] = useState<ColorTheme>(() => readStoredColorTheme())

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed(prev => !prev)
  }, [])

  const toggleThemeMode = useCallback(() => {
    setThemeMode(prev => (prev === 'light' ? 'dark' : 'light'))
  }, [])

  const setColorTheme = useCallback((theme: ColorTheme) => {
    setColorThemeState(theme)
  }, [])

  useLayoutEffect(() => {
    applyThemeToDocument(themeMode, colorTheme)
    localStorage.setItem(THEME_MODE_KEY, themeMode)
    localStorage.setItem(COLOR_THEME_KEY, colorTheme)
  }, [themeMode, colorTheme])

  return (
    <LayoutContext.Provider
      value={{
        sidebarCollapsed,
        toggleSidebar,
        setSidebarCollapsed,
        themeMode,
        colorTheme,
        toggleThemeMode,
        setColorTheme,
      }}
    >
      {children}
    </LayoutContext.Provider>
  )
}

export const useLayoutContext = () => useContext(LayoutContext)
